/**
 * A list read one page at a time through the API's keyset cursor.
 *
 * The list routes answer `{ data, meta: { cursor, hasMore } }`, where
 * `meta.total` is the number of rows on THIS page, not in the list. A page
 * that titles its table with that number tells a person a page of 50 is the
 * whole list (D140). This hook walks the cursor instead: `next` asks for the
 * page after this one, `previous` goes back, and `hasMore` says whether more
 * rows follow, so the screen never has to guess.
 *
 * The first page keeps the query key the page was given; a later page adds
 * its cursor to the key. Changing the key (a new filter) starts again at the
 * first page.
 */
import { useState } from "react";
import { useApiList } from "./hooks";

/** `path` with each non-empty parameter added to its query string. */
export function withQuery(
  path: string,
  params: Record<string, string | undefined>,
): string {
  const query = new URLSearchParams();
  for (const [name, value] of Object.entries(params))
    if (value) query.set(name, value);
  const text = query.toString();
  if (!text) return path;
  return `${path}${path.includes("?") ? "&" : "?"}${text}`;
}

export function withCursor(path: string, cursor: string | undefined): string {
  return withQuery(path, { cursor });
}

export function useCursorList<T>(
  key: string[],
  path: string,
  options?: Parameters<typeof useApiList<T>>[2],
) {
  const scope = JSON.stringify([key, path]);
  const [walk, setWalk] = useState<{ scope: string; cursors: string[] }>({
    scope,
    cursors: [],
  });
  // A new filter is a new list: its walk starts again at page 1.
  const cursors = walk.scope === scope ? walk.cursors : [];
  const cursor = cursors[cursors.length - 1];
  const query = useApiList<T>(
    cursor ? [...key, "cursor", cursor] : key,
    withCursor(path, cursor),
    options,
  );
  const meta = (query.data?.meta ?? {}) as {
    cursor?: string | null;
    hasMore?: boolean;
  };
  const nextCursor =
    typeof meta.cursor === "string" && meta.cursor !== "" ? meta.cursor : null;
  return {
    ...query,
    /** 1 for the first page. */
    page: cursors.length + 1,
    /** More rows follow this page. */
    hasMore: meta.hasMore === true || nextCursor !== null,
    next: nextCursor
      ? () => setWalk({ scope, cursors: [...cursors, nextCursor] })
      : undefined,
    previous:
      cursors.length > 0
        ? () => setWalk({ scope, cursors: cursors.slice(0, -1) })
        : undefined,
  };
}
